import type { IExchangeRate, PriceDisplayMode } from '../types/storefront.types';

export const parseDecimal = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? 0 : num;
};

export const formatCurrency = (amount: number, currencyCode?: string): string => {
  const symbol = currencyCode === 'VES' ? 'Bs.' : '$';
  return `${symbol} ${amount.toFixed(2)}`;
};

export const formatBs = (amount: number): string => `Bs. ${amount.toFixed(2)}`;

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return new Intl.DateTimeFormat('es-VE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatShortDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return new Intl.DateTimeFormat('es-VE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/**
 * Símbolo de la moneda de referencia de la tienda.
 * Centraliza el ternario `currencyCode === 'USD' ? '$' : '€'` que estaba
 * repetido en cada vista, para que un cambio de moneda no exija tocar 17 archivos.
 */
export const getCurrencySymbol = (currencyCode?: string): string =>
  currencyCode === 'USD' ? '$' : currencyCode === 'VES' ? 'Bs.' : '€';

/** Formatea un monto en la moneda de referencia: `$ 12.40`. */
export const formatMoney = (amount: number, currencyCode?: string): string =>
  `${getCurrencySymbol(currencyCode)} ${amount.toFixed(2)}`;

/** Convierte y formatea un monto USD a bolívares con la tasa vigente. */
export const formatToVes = (amountUsd: number, rateValue?: number): string | null =>
  rateValue ? `Bs. ${(amountUsd * rateValue).toFixed(2)}` : null;

export interface FormattedPrice {
  primary: string;
  secondary?: string;
}

/**
 * Único formateador de precios de catálogo/carrito/checkout según el modo del partner.
 * Solo afecta la visualización: los montos enviados al backend no cambian.
 * Sin tasa no hay VES posible, así que cae a solo divisa aunque el modo diga otra cosa.
 */
export const formatPrice = (
  amount: number,
  rate: IExchangeRate | null | undefined,
  mode: PriceDisplayMode = 'BOTH'
): FormattedPrice => {
  const ref = formatMoney(amount, rate?.currencyCode ?? 'USD');
  if (!rate || mode === 'EXCHANGE_RATE_ONLY') return { primary: ref };
  const local = formatBs(amount * rate.rateValue);
  if (mode === 'LOCAL_ONLY') return { primary: local };
  return { primary: ref, secondary: local };
};
